/**
 * `window.onerror` + `unhandledrejection`
 * handlers wired at app boot.
 *
 * Errors that escape the React tree (a `setTimeout` callback, a Web
 * Worker postback, a microtask rejection, an inline event handler that
 * throws outside React's error-boundary contract) go straight to the
 * console by default — invisible to the user. This module installs
 * two `window` listeners that route those escapes through the toast
 * layer so the user at least sees that something went wrong and has a
 * one-click "Copy report" path.
 *
 * The handlers are deliberately defensive:
 *
 *  - they never `preventDefault()` on the underlying event — the
 *  browser-level console.error noise is preserved for devtools;
 *  - they dedupe by message + a 5-second window so a tight loop
 *  (e.g. a thrown render that fires every frame) doesn't flood the
 *  toast stack;
 *  - they never escalate — no full-screen takeover, just a polite
 *  toast that the user can dismiss.
 *
 * Toast delivery is bridged through a module-level setter
 * ({@link setGlobalToastDispatcher}) that the React app calls after
 * mount (see `src/next/GlobalErrorToasts.tsx`). The LISTENERS are
 * installed from `src/main.tsx` before React mounts, so an escape
 * during boot is still logged; until the bridge is set the toast is
 * dropped, because there is no surface to show it on yet and
 * `ErrorBoundaryNext` owns that case.
 *
 * The dispatcher is described STRUCTURALLY rather than by importing
 * the toast module: this file is plain TypeScript in `src/utils`, and
 * it must not pull a React module into anything that imports it.
 */

import { errorLog } from "./errorLog";
import { buildErrorReport, copyErrorReport } from "./errorReporter";

/**
 * Bridge to the in-tree toast API. The App component calls
 * `setGlobalToastDispatcher` after mount to wire up; the bridge is
 * cleared on unmount so a stale React state setter doesn't fire.
 */
let activeDispatcher: GlobalToastDispatcher | null = null;

/** The copy this module needs, supplied already localized by the bridge. */
export interface GlobalErrorToastCopy {
  /** Notice text for an uncaught error. */
  unexpected: string;
  /** Notice text for an unhandled promise rejection. */
  rejection: string;
  /** Action button label; copies the JSON error report to the clipboard. */
  copyReport: string;
  /** Notice text when that copy did not land. */
  copyFailed: string;
}

export interface GlobalToastDispatcher {
  /**
   * Mirrors `useToast().toast` in src/next/Toast.tsx: one already-localized
   * line, a tone, and an optional single action.
   */
  toast: (
    text: string,
    tone: "bad",
    action?: { label: string; onClick: () => void },
  ) => void;
  copy: GlobalErrorToastCopy;
}

export function setGlobalToastDispatcher(dispatcher: GlobalToastDispatcher | null): void {
  activeDispatcher = dispatcher;
}

/**
 * Throttle map — message → last fire time (ms). Resets implicitly
 * when the throttle window elapses; we don't bother to clean up
 * stale entries because the cardinality is tiny in practice (an app
 * that's actively crashing in two distinct ways is already lost).
 */
const lastFiredAt = new Map<string, number>();
const THROTTLE_WINDOW_MS = 5000;

function shouldFire(message: string, now: number): boolean {
  const last = lastFiredAt.get(message);
  if (last !== undefined && now - last < THROTTLE_WINDOW_MS) return false;
  lastFiredAt.set(message, now);
  return true;
}

/** Test seam: reset the throttle map between tests. */
export function resetGlobalErrorThrottle(): void {
  lastFiredAt.clear();
}

function handleError(message: string, error: unknown, isRejection: boolean): void {
  errorLog(isRejection ? "unhandledrejection" : "window.onerror", error);
  const dispatcher = activeDispatcher;
  if (!dispatcher) return;
  const now = Date.now();
  if (!shouldFire(message || "(no message)", now)) return;
  const report = buildErrorReport(error);
  const copy = dispatcher.copy;
  dispatcher.toast(isRejection ? copy.rejection : copy.unexpected, "bad", {
    label: copy.copyReport,
    onClick: () => {
      // Clicking the action also DISMISSES the toast, so a dropped `false`
      // took the notice, the action and the report away together and left
      // the clipboard untouched. Say so, on whatever dispatcher is live at
      // that moment: the click can land after a locale switch has swapped
      // the bridge out from under this closure.
      void copyErrorReport(report).then((ok) => {
        if (ok) return;
        const live = activeDispatcher ?? dispatcher;
        live.toast(live.copy.copyFailed, "bad");
      });
    },
  });
}

let installed = false;
let onErrorListener: ((event: ErrorEvent) => void) | null = null;
let onRejectionListener: ((event: PromiseRejectionEvent) => void) | null = null;

/**
 * Idempotent install. Calling more than once (HMR, two ReactDOM
 * roots in tests) is a no-op after the first.
 */
export function installGlobalErrorHandlers(): void {
  if (installed) return;
  if (typeof window === "undefined") return;
  installed = true;
  onErrorListener = (event: ErrorEvent) => {
    // The "error" event covers both synchronous render throws (rare
    // in practice — React's own error boundary swallows them) and
    // every uncaught throw inside non-React code: setTimeout
    // callbacks, worker messages, raw event handlers, etc.
    const err = event.error ?? new Error(event.message || "Unknown error");
    handleError(event.message || (err as Error).message || "Unknown error", err, false);
  };
  onRejectionListener = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message || reason.name
        : typeof reason === "string"
          ? reason
          : "Unhandled promise rejection";
    handleError(message, reason, true);
  };
  window.addEventListener("error", onErrorListener);
  window.addEventListener("unhandledrejection", onRejectionListener);
}

/**
 * Test seam: tear down the listeners so each test starts clean.
 * In production we never uninstall — the handlers run for the
 * lifetime of the page.
 */
export function uninstallGlobalErrorHandlers(): void {
  if (!installed) return;
  installed = false;
  if (typeof window === "undefined") return;
  if (onErrorListener) window.removeEventListener("error", onErrorListener);
  if (onRejectionListener)
    window.removeEventListener("unhandledrejection", onRejectionListener);
  onErrorListener = null;
  onRejectionListener = null;
}
